import React from 'react';
import { useIntl } from 'react-intl';
import splitOnFirst from 'split-on-first';
import { getAttributeDescription } from '@printdeal/business-logic';
import { mapAttributesToMcpAttributes } from '@printdeal/transformers';

import { DeliveryToolSettings } from '../../../../../../types/file-delivery/FileDelivery';
import LineItem from '../../../../../../types/cart/LineItem';

import FileDeliveryButton from '../../../../../file-delivery/FileDeliveryButton';
import { convertNullToUndefined } from '../../../../../../types/util';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';
import { getActualEditorType, getSelectedDeliveryTool, getFileDeliveryItemId } from '../../../../../../helpers/LineItems';

interface OpenDeliveryToolButtonProps {
  children: JSX.Element;
  lineItem: LineItem,
  orderId: string,
  deliveryToolSettings: DeliveryToolSettings,
  chosenDeliveryMethod?: string;
  isEdit?: boolean,
  source?: 'ODP' | 'Account',
}

const OpenDeliveryToolButton = ({
  children,
  lineItem,
  orderId,
  deliveryToolSettings,
  chosenDeliveryMethod,
  isEdit = false,
  source = 'ODP',
}: OpenDeliveryToolButtonProps) => {
  const { locale } = useIntl();
  const fileDeliveryApi = ServiceFactory.createFileDeliveryService();

  const {
    productKey,
    productVersion,
    networkSku,
    variableAttributes,
    mcpDocumentsSource,
    merchandisingProductName,
    localizedProductName,
    lineItemNumber,
    editorType,
    mcpSku,
  } = lineItem.custom.fields;

  const requestedDeliveryTool = chosenDeliveryMethod || editorType;
  const fileDeliveryItemId = getFileDeliveryItemId(lineItem);

  const callbackUrl = `${process.env.GATSBY_FILE_DELIVERY_URL}${fileDeliveryApi.getUpdateDocumentSourceUrl(
    fileDeliveryItemId,
    orderId,
    getActualEditorType(requestedDeliveryTool, deliveryToolSettings),
  )}`;
  const mcpAttributes = JSON.stringify(mapAttributesToMcpAttributes(variableAttributes));
  const alternativeUploadPath = `${window.location.pathname.includes(orderId)
    ? `${window.location.pathname}`
    : `${window.location.pathname.replace(/\/\+$/, '')}/${orderId}`}/upload/${fileDeliveryItemId}`;

  return (
    <FileDeliveryButton
      lineItemId={fileDeliveryItemId}
      deliveryToolSettings={deliveryToolSettings}
      selectedDeliveryTool={getSelectedDeliveryTool(requestedDeliveryTool, deliveryToolSettings)}
      mcpSKU={networkSku || mcpSku}
      variantSku={lineItem.variant.sku}
      productKey={productKey}
      productVersion={productVersion}
      mcpAttributes={mcpAttributes}
      documentReference={(isEdit && mcpDocumentsSource && splitOnFirst(mcpDocumentsSource[0], ':')[1]) || undefined}
      callbackUrl={callbackUrl}
      lineItemNumber={lineItemNumber}
      lineItemDescription={convertNullToUndefined(getAttributeDescription(lineItem, locale.slice(0, 2)))}
      defaultDesignName={localizedProductName?.[locale.slice(0, 2)] || merchandisingProductName}
      alternativeUploadPath={alternativeUploadPath}
      orderId={orderId}
      price={lineItem.taxedPrice}
      source={source}
    >
      {children}
    </FileDeliveryButton>
  );
};

export default OpenDeliveryToolButton;
